import Impostor from '../rendering/Impostor.js';

/**
 * WorldItem - A loot item dropped in the world, rendered as an Impostor sprite.
 * Hovers and bobs to attract attention. Auto-collected on player proximity.
 */
export default class WorldItem {
  static SIZE = 0.3;
  static HOVER_AMPLITUDE = 0.15;
  static HOVER_SPEED = 2.0;
  static PICKUP_RANGE = 1.2;

  /**
   * @param {Object} options
   * @param {string} options.itemId - ID from ItemDefinitions
   * @param {Object} options.def - Full item definition
   * @param {number} options.x
   * @param {number} options.y - Ground level Y
   * @param {number} options.z
   * @param {number} [options.quantity=1]
   * @param {Object} [options.instanceData=null] - Level-scaled stats from ItemFactory
   */
  constructor(options) {
    this.id = `item_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    this.itemId = options.itemId;
    this.def = options.def;
    this.x = options.x;
    this.baseY = options.y;
    this.y = options.y + 0.3;
    this.z = options.z;
    this.quantity = options.quantity || 1;
    this.instanceData = options.instanceData || null;
    this.collected = false;

    // Pickup immunity timer (seconds) — prevents instant re-collection when dropped by player
    this._pickupImmunity = 0;

    // Animation state
    this._hoverTime = Math.random() * Math.PI * 2;

    /** @type {Impostor|null} */
    this.impostor = null;
  }

  /**
   * Create the Impostor visual and add to world.
   * @param {World} world
   * @returns {Impostor}
   */
  createVisual(world) {
    // Use item's pixel art icon; fall back to generated sprite if no icon defined
    let textureUrl;
    if (this.def.icon) {
      textureUrl = this.def.icon;
    } else {
      const c = this.def.color || { r: 255, g: 255, b: 255 };
      textureUrl = WorldItem._generateItemSprite(c.r, c.g, c.b, this.def.type);
    }

    this.impostor = new Impostor({
      id: this.id,
      x: this.x,
      y: this.y,
      z: this.z,
      textureUrl,
      spriteWidth: 32,
      spriteHeight: 32,
      scale: 0.6
    });

    world.addImpostor(this.impostor);

    // Show stack count badge for quantities > 1
    if (this.quantity > 1) {
      this._addQuantityBadge();
    }

    return this.impostor;
  }

  /**
   * Add a quantity badge to the impostor's DOM element.
   * @private
   */
  _addQuantityBadge() {
    if (!this.impostor?.element) return;
    const badge = document.createElement('span');
    badge.className = 'world-item-qty';
    badge.textContent = this.quantity;
    this.impostor.element.appendChild(badge);
    this._qtyBadge = badge;
  }

  /**
   * Update hover animation each frame.
   */
  update(deltaTime) {
    if (this.collected) return;
    if (this._pickupImmunity > 0) this._pickupImmunity -= deltaTime;
    this._hoverTime += deltaTime * WorldItem.HOVER_SPEED;
    this.y = this.baseY + 0.3 + Math.sin(this._hoverTime) * WorldItem.HOVER_AMPLITUDE;

    // Update impostor position
    if (this.impostor) {
      this.impostor.y = this.y;
      // Mark for re-render
      if (this.impostor.element) {
        this.impostor._needsUpdate = true;
      }
    }
  }

  /**
   * Generate a simple data-URL sprite for an item (colored diamond/circle).
   * @private
   */
  static _spriteCache = new Map();

  static _generateItemSprite(r, g, b, type) {
    const key = `${r},${g},${b},${type}`;
    if (WorldItem._spriteCache.has(key)) {
      return WorldItem._spriteCache.get(key);
    }

    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');

    // Draw a glowing diamond shape
    ctx.save();
    ctx.translate(16, 16);

    // Outer glow
    ctx.shadowColor = `rgb(${r},${g},${b})`;
    ctx.shadowBlur = 8;

    // Diamond shape
    ctx.beginPath();
    ctx.moveTo(0, -10);
    ctx.lineTo(8, 0);
    ctx.lineTo(0, 10);
    ctx.lineTo(-8, 0);
    ctx.closePath();

    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fill();

    // Highlight
    ctx.fillStyle = `rgba(255,255,255,0.4)`;
    ctx.beginPath();
    ctx.moveTo(0, -8);
    ctx.lineTo(4, 0);
    ctx.lineTo(0, 2);
    ctx.lineTo(-4, 0);
    ctx.closePath();
    ctx.fill();

    ctx.restore();

    const url = canvas.toDataURL();
    WorldItem._spriteCache.set(key, url);
    return url;
  }
}
